"use client"

import { openMeetingEntry } from "utils/meetingEntry"

import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Check } from "lucide-react"

import EventsListCard, { EventsListItem, type Attachment, type EventsListCardHandle } from "components/mentorship/EventsListCard"
import {
  CohortEventHero,
  CohortFailureState,
  CohortLoadingState,
} from "components/mentorship/CohortExperience"
import { CohortOverviewCard } from "components/mentorship/admin/CohortOverviewCard"
import { FlagsCard } from "components/mentorship/admin/FlagsCard"
import { MessagesCard } from "components/mentorship/admin/MessagesCard"
import { MentorPairingsCard } from "components/mentorship/admin/MentorPairingsCard"
import { PersonSidePanel } from "components/mentorship/admin/PersonSidePanel"
import { ProgramCard } from "components/mentorship/admin/ProgramCard"
import { ReportsTrendsCard } from "components/mentorship/admin/ReportsTrendsCard"
import { RosterCard } from "components/mentorship/admin/RosterCard"
import { TodosCard } from "components/mentorship/admin/TodosCard"
import { ChecklistSummary, Person, SafetySignal } from "components/mentorship/admin/types"
import { FlowPicker } from "components/admin/FlowPicker"
import { AppDialog } from "components/ui/app-dialog"
import { Button } from "components/ui/button"
import {
  DialogClose,
} from "components/ui/dialog"
import { Input } from "components/ui/input"
import { Label } from "components/ui/label"
import { Textarea } from "components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { useToast } from "components/ui/use-toast"
import { useAuth } from "contexts/AuthContext"
import { adminContent, flows as flowApi, lifeScores, media, safetySignals } from "services/api"
import { meetings } from "services/api/meetingsClient"
import type { LifeScoresReport, MediaAsset } from "types"
import { getPrimaryCohort } from "utils/cohortSelection"
import { getCohortRoleLabel, getCohortRoleLabels, getInviteeBucketLabel, lowerLabel } from "utils/cohortRoleLabels"
import { ForNextTimeDialog } from "components/mentorship/continuity/ForNextTimeDialog"
import { getContinuityCue, useMentorshipRelationships } from "components/mentorship/continuity/useMentorshipRelationships"
import { RelationshipMeetingActions } from "components/mentorship/RelationshipMeetingActions"

type FlowSummary = {
  id: number
  name: string
  description?: string
  is_active?: boolean
}

type DashboardCohort = {
  id: string
  name: string
  description?: string | null
  is_active?: boolean
  is_current?: boolean
  role?: "mentee" | "mentor" | "cohort_director" | "cohort_admin"
  mentee_label_singular?: string | null
  mentee_label_plural?: string | null
  mentor_label_singular?: string | null
  mentor_label_plural?: string | null
  start_date?: string | null
  end_date?: string | null
}

const NO_FLOW_VALUE = "none"
type ChecklistAttachmentMode = "flow" | "link" | "file" | "none"
type AdminMobileTab = "attention" | "manage" | "people"

const ADMIN_MOBILE_TABS: ReadonlyArray<{ id: AdminMobileTab; label: string }> = [
  { id: "attention", label: "Attention" },
  { id: "manage", label: "Manage" },
  { id: "people", label: "People" },
]

const getAdminMobileTabFromHash = (hash: string): AdminMobileTab => {
  if (["#admin-checklist", "#admin-mentor-pairings", "#admin-messages"].includes(hash)) {
    return "manage"
  }
  if (["#admin-roster", "#admin-reports"].includes(hash)) {
    return "people"
  }
  return "attention"
}

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data
  if (typeof data === "string" && data.trim()) return data
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail
  if (typeof data?.error === "string" && data.error.trim()) return data.error
  if (typeof data?.message === "string" && data.message.trim()) return data.message
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.filter((value: unknown) => typeof value === "string").join(" ")
  }

  if (data && typeof data === "object") {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (typeof value === "string" && value.trim()) return value
      if (Array.isArray(value)) {
        const firstString = value.find((item): item is string => typeof item === "string" && item.trim().length > 0)
        if (firstString) return firstString
      }
    }
  }

  if (typeof error?.message === "string" && error.message.trim()) return error.message
  return fallback
}

const normalizeExternalUrl = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

const normalizeDashboardCohort = (value: any): DashboardCohort | null => {
  if (!value?.id) return null
  return {
    id: String(value.id),
    name: value.name || "Selected cohort",
    description: value.description ?? null,
    is_active: value.is_active,
    is_current: value.is_current,
    role: value.role,
    mentee_label_singular: value.mentee_label_singular ?? null,
    mentee_label_plural: value.mentee_label_plural ?? null,
    mentor_label_singular: value.mentor_label_singular ?? null,
    mentor_label_plural: value.mentor_label_plural ?? null,
    start_date: value.start_date ?? null,
    end_date: value.end_date ?? null,
  }
}

export default function AdminPage() {
  const [rosterSearch, setRosterSearch] = useState("")
  const [filterFlags, setFilterFlags] = useState<"new" | "in-progress" | "complete">("new")
  const [messageRecipient, setMessageRecipient] = useState<"individual" | "mentors" | "cohort" | "scholars">(
    "individual",
  )
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [selectedSignal, setSelectedSignal] = useState<SafetySignal | null>(null)
  const [messageChannels, setMessageChannels] = useState<"email" | "text" | "email-text">("email")
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [selectedIndividuals, setSelectedIndividuals] = useState<string[]>([])
  const [events, setEvents] = useState<EventsListItem[]>([])
  const [eventsLoading, setEventsLoading] = useState(true)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [lifeScoresReport, setLifeScoresReport] = useState<LifeScoresReport | null>(null)
  const [lifeScoresLoading, setLifeScoresLoading] = useState(false)
  const [lifeScoresError, setLifeScoresError] = useState<string | null>(null)
  const [lifeScoresUserId, setLifeScoresUserId] = useState<string>("all")
  const [rosterPeople, setRosterPeople] = useState<Person[]>([])
  const [safetySignalsList, setSafetySignalsList] = useState<SafetySignal[]>([])
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const [cohortRecoveryNotice, setCohortRecoveryNotice] = useState<string | null>(null)
  const [dashboardCohort, setDashboardCohort] = useState<DashboardCohort | null>(null)
  const [showPastEvents, setShowPastEvents] = useState(false)
  const [showRequiresAttendance, setShowRequiresAttendance] = useState(false)
  const [checklistSummary, setChecklistSummary] = useState<ChecklistSummary | null>(null)
  const [checklistRole, setChecklistRole] = useState<"mentors" | "scholars">("mentors")
  const [checklistModalOpen, setChecklistModalOpen] = useState(false)
  const [checklistSaving, setChecklistSaving] = useState(false)
  const [checklistError, setChecklistError] = useState("")
  const [signalActionLoading, setSignalActionLoading] = useState(false)
  const [signalNoteDraft, setSignalNoteDraft] = useState("")
  const [signalNoteSaving, setSignalNoteSaving] = useState(false)
  const [signalNoteError, setSignalNoteError] = useState("")
  const [signalConfirmAction, setSignalConfirmAction] = useState<"notify" | "dismiss" | null>(null)
  const [checklistMode, setChecklistMode] = useState<"create" | "edit">("create")
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
  const [checklistMediaAsset, setChecklistMediaAsset] = useState<MediaAsset | null>(null)
  const [checklistMediaUploading, setChecklistMediaUploading] = useState(false)
  const [checklistMediaError, setChecklistMediaError] = useState("")
  const [checklistAttachmentMode, setChecklistAttachmentMode] = useState<ChecklistAttachmentMode>("none")
  const checklistMediaInputRef = useRef<HTMLInputElement | null>(null)
  const eventsCardRef = useRef<EventsListCardHandle | null>(null)
  const [flows, setFlows] = useState<FlowSummary[]>([])
  const [loadingFlows, setLoadingFlows] = useState(false)
  const [flowSearchTerm, setFlowSearchTerm] = useState("")
  const [checklistDraft, setChecklistDraft] = useState({
    title: "",
    description: "",
    dueDate: "",
    externalUrl: "",
    questionFlowId: NO_FLOW_VALUE,
    role: "mentors" as "mentors" | "scholars",
  })
  const { user: currentUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeMobileTab, setActiveMobileTab] = useState<AdminMobileTab>(() => (
    getAdminMobileTabFromHash(location.hash)
  ))
  const mobileTabRefs = useRef<Record<AdminMobileTab, HTMLButtonElement | null>>({
    attention: null,
    manage: null,
    people: null,
  })
  const [messageBody, setMessageBody] = useState("")
  const [messageSubject, setMessageSubject] = useState("")
  const { addToast } = useToast()

  useEffect(() => {
    setActiveMobileTab(getAdminMobileTabFromHash(location.hash))
  }, [location.hash])

  const selectMobileTab = (nextTab: AdminMobileTab) => {
    setActiveMobileTab(nextTab)
    requestAnimationFrame(() => {
      document.getElementById(`admin-mobile-panel-${nextTab}`)?.scrollIntoView?.({ block: "start" })
    })
  }

  const handleMobileTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    let nextIndex: number | null = null
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % ADMIN_MOBILE_TABS.length
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + ADMIN_MOBILE_TABS.length) % ADMIN_MOBILE_TABS.length
    if (event.key === "Home") nextIndex = 0
    if (event.key === "End") nextIndex = ADMIN_MOBILE_TABS.length - 1
    if (nextIndex === null) return

    event.preventDefault()
    const nextTab = ADMIN_MOBILE_TABS[nextIndex].id
    selectMobileTab(nextTab)
    mobileTabRefs.current[nextTab]?.focus()
  }

  const toMediaType = (value?: string | null) => {
    if (value === "image" || value === "video" || value === "pdf" || value === "audio") {
      return value
    }
    return "other"
  }

  const parseCohortDate = (isoDate?: string | null) => {
    if (!isoDate) return null
    const dateOnlyMatch = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const monthIndex = Number(dateOnlyMatch[2]) - 1
      const day = Number(dateOnlyMatch[3])
      const localDate = new Date(year, monthIndex, day)
      return Number.isNaN(localDate.getTime()) ? null : localDate
    }
    const date = new Date(isoDate)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const cohortIdParam = useMemo(() => {
    return new URLSearchParams(location.search).get("cohortId")
  }, [location.search])

  const membershipCohorts = useMemo(() => {
    return (currentUser?.cohorts ?? [])
      .map((cohort) => normalizeDashboardCohort(cohort))
      .filter((cohort): cohort is DashboardCohort => Boolean(cohort))
  }, [currentUser?.cohorts])

  const primaryCohort = useMemo(() => {
    const cohorts = currentUser?.cohorts ?? []
    // Default to a cohort this user directs or administers; their current cohort may be one they only mentor in.
    const managed = currentUser?.is_staff
      ? []
      : cohorts.filter((cohort) => cohort.role === "cohort_director" || cohort.role === "cohort_admin")
    return normalizeDashboardCohort(getPrimaryCohort(managed.length ? managed : cohorts))
  }, [currentUser?.cohorts, currentUser?.is_staff])

  const requestedCohortIsUnavailable = Boolean(
    cohortIdParam
    && currentUser
    && !currentUser.is_staff
    && !membershipCohorts.some((cohort) => cohort.id === cohortIdParam),
  )
  const fallbackCohort = primaryCohort ?? membershipCohorts[0] ?? null
  const selectedCohortId = requestedCohortIsUnavailable
    ? fallbackCohort?.id || null
    : cohortIdParam || primaryCohort?.id || null

  const selectedMembershipCohort = useMemo(() => {
    if (!selectedCohortId) return null
    return membershipCohorts.find((cohort) => cohort.id === selectedCohortId) ?? null
  }, [membershipCohorts, selectedCohortId])

  const activeCohort = useMemo(() => {
    if (dashboardCohort && (!selectedCohortId || dashboardCohort.id === selectedCohortId)) {
      return dashboardCohort
    }
    return selectedMembershipCohort ?? primaryCohort ?? dashboardCohort
  }, [dashboardCohort, primaryCohort, selectedCohortId, selectedMembershipCohort])

  const cohortSwitcherOptions = useMemo(() => {
    const options = [...membershipCohorts]
    if (dashboardCohort && !options.some((cohort) => cohort.id === dashboardCohort.id)) {
      options.push(dashboardCohort)
    }
    return options
  }, [dashboardCohort, membershipCohorts])

  const cohortRoleLabels = activeCohort || null
  const { relationships: continuityRelationships, error: continuityError, refresh: refreshContinuity } = useMentorshipRelationships(activeCohort?.id)
  const [continuityRelationshipId, setContinuityRelationshipId] = useState<string | undefined>()
  const [continuityOpen, setContinuityOpen] = useState(false)
  const openContinuity = (relationshipId: string) => {
    setContinuityRelationshipId(relationshipId)
    setContinuityOpen(true)
  }
  const configuredRoleLabels = getCohortRoleLabels(cohortRoleLabels)

  const cohortStartDate = useMemo(
    () => parseCohortDate(activeCohort?.start_date),
    [activeCohort?.start_date],
  )

  const cohortEndDate = useMemo(
    () => parseCohortDate(activeCohort?.end_date),
    [activeCohort?.end_date],
  )

  const meetingIdParam = useMemo(() => {
    return new URLSearchParams(location.search).get("meetingId")
  }, [location.search])

  const handleDashboardCohortChange = useCallback(
    (nextCohortId: string) => {
      setCohortRecoveryNotice(null)
      const params = new URLSearchParams(location.search)
      params.set("cohortId", nextCohortId)
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace: false },
      )
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  const buildMeetingDetailsUrl = useCallback(
    (meetingId: string) => {
      const params = new URLSearchParams()
      if (selectedCohortId) {
        params.set("cohortId", selectedCohortId)
      }
      params.set("meetingId", meetingId)
      return `/mentorship/director?${params.toString()}`
    },
    [selectedCohortId],
  )

  const handleMeetingPopupUrlChange = useCallback(
    (meetingId: string | null) => {
      const params = new URLSearchParams(location.search)
      if (meetingId) {
        params.set("meetingId", meetingId)
      } else {
        params.delete("meetingId")
      }
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace: !meetingId },
      )
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  const formatInputDate = (date?: Date | null) => {
    if (!date) return ""
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const minChecklistDueDate = useMemo(() => {
    return formatInputDate(new Date())
  }, [])

  const formatDateLabel = (iso: string) => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ""
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }

  const formatTimeLabel = (startIso?: string, endIso?: string) => {
    if (!startIso) return ""
    const start = new Date(startIso)
    if (Number.isNaN(start.getTime())) return ""
    if (endIso) {
      const end = new Date(endIso)
      if (!Number.isNaN(end.getTime())) {
        const fmt = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
        return `${fmt(start)} - ${fmt(end)}`
      }
    }
    return start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  }

  const formatSignalTimestamp = (value?: string | null) => {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    const datePart = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
    return `${datePart} ${timePart}`
  }

  const normalizeRosterPerson = (u: any): Person => {
    const roleKey = (u.role || "mentee").toLowerCase().replace(/\s+/g, "_")
    const normalizedRole = getCohortRoleLabel(roleKey, cohortRoleLabels)
    const mapRecentEvents = Array.isArray(u.recent_events)
      ? u.recent_events.map((ev: any) => ({
          id: String(ev.id),
          title: ev.title,
          startTime: ev.start_time,
          endTime: ev.end_time,
          status: ev.status,
        }))
      : []
    const mapOpenTodos = Array.isArray(u.open_todos)
      ? u.open_todos.map((todo: any) => ({
          id: String(todo.id),
          title: todo.title,
          dueDate: todo.due_date,
          completed: !!todo.completed,
        }))
      : []
    return {
      id: Number(u.id),
      name: u.display_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "Unknown",
      initials: u.initials || (u.display_name || "NA").slice(0, 2).toUpperCase(),
      avatarUrl: u.profile?.avatar_url || u.avatar_url || null,
      email: u.email || null,
      mentorId: u.mentor_id ? Number(u.mentor_id) : null,
      mentorName: u.mentor_name || null,
      role: normalizedRole,
      roleKey: roleKey as Person["roleKey"],
      smsCapable: Boolean(u.sms_capable),
      smsUnavailableReason: u.sms_unavailable_reason ?? null,
      isMyMentor: Boolean(u.is_mentor_for_viewer || u.is_my_mentor),
      status: "on-track",
      adminNote: u.admin_note || "",
      recentEvents: mapRecentEvents,
      openFlags: [],
      openTodos: mapOpenTodos,
      taskCompletion: {
        completed: Number(u?.task_completion?.completed || 0),
        total: Number(u?.task_completion?.total || mapOpenTodos.length),
      },
    }
  }

  const emptyChecklistSummary: ChecklistSummary = {
    mentors: { totalUsers: 0, templates: [] },
    scholars: { totalUsers: 0, templates: [] },
  }

  const normalizeChecklistSummary = (summary: any): ChecklistSummary => {
    if (!summary) return emptyChecklistSummary
    const normalizeDateString = (value?: string | null) => {
      if (!value) return null
      return value.split("T")[0]
    }
    const mapRole = (roleSummary: any) => ({
      totalUsers: Number(roleSummary?.total_users || 0),
      templates: Array.isArray(roleSummary?.templates)
        ? roleSummary.templates.map((template: any) => ({
            id: String(template.id),
            title: template.title || "Untitled",
            description: template.description || "",
            dueOffsetDays: template.due_offset_days ?? null,
            externalUrl: template.external_url ?? null,
            completedCount: Number(template.completed_count || 0),
            dueDate: normalizeDateString(template.due_date),
            dueDateVaries: Boolean(template.due_date_varies),
            mediaAsset: template.media_asset ?? null,
            users: Array.isArray(template.users)
              ? template.users.map((user: any) => ({
                  id: String(user.id),
                  name: user.name || "Unknown",
                  completed: Boolean(user.completed),
                  completedAt: user.completed_at ?? null,
                }))
              : [],
          }))
        : [],
    })
    return {
      mentors: mapRole(summary.mentors),
      scholars: mapRole(summary.scholars),
    }
  }

  const loadDashboard = async (silent = false) => {
    if (!silent) {
      setDashboardLoading(true)
      setEventsLoading(true)
    }
    setDashboardError(null)
    setEventsError(null)
    try {
      const data = await meetings.getAdminDashboard(selectedCohortId ? { cohort_id: selectedCohortId } : undefined)
      setDashboardCohort(normalizeDashboardCohort((data as any)?.cohort))
      const meetingList = (Array.isArray((data as any)?.meetings) ? (data as any).meetings : [])
        .filter((meeting: any) => meeting?.status !== "cancelled" && meeting?.occurrence_status !== "cancelled")
      const displayNameFromKey = (original?: string | null, key?: string | null, fallback?: string) => {
        const trimmed = original?.trim()
        if (trimmed) return trimmed
        const source = key || ""
        if (source) {
          const parts = source.split(/[/\\]/).filter(Boolean)
          if (parts.length) return parts[parts.length - 1]
        }
        return fallback || "Attachment"
      }
      const mapAttachments = (resources: any[] = []) =>
        resources.map((res) => ({
          key: res.storage_key || String(res.media_asset || res.id),
          name: displayNameFromKey(res.original_filename, res.storage_key, res.storage_key || "Attachment"),
          mime: res.media_type,
          assetId: res.media_asset ? String(res.media_asset) : undefined,
          originalFilename: res.original_filename,
        }))
      const mappedEvents: EventsListItem[] = meetingList.map((item: any) => {
        const invitedUserIds = Array.isArray(item.invited_user_ids)
          ? item.invited_user_ids.map((id: string | number) => String(id))
          : []
        const explicitMode =
          item.invitees_mode === "cohort" ||
          item.invitees_mode === "mentors" ||
          item.invitees_mode === "scholars" ||
          item.invitees_mode === "custom"
            ? item.invitees_mode
            : undefined
        const inviteesMode = explicitMode
          ? explicitMode
          : invitedUserIds.includes("__COHORT__")
            ? "cohort"
            : invitedUserIds.includes("__MENTORS__")
              ? "mentors"
              : invitedUserIds.includes("__SCHOLARS__")
                ? "scholars"
                : "custom"
        const recurrence = item.recurrence
          ? {
              interval: item.recurrence.interval,
              period: item.recurrence.period,
              daysOfWeek: item.recurrence.days_of_week || item.recurrence.daysOfWeek,
              startDate: item.recurrence.start_date || item.recurrence.startDate,
              endDate: item.recurrence.end_date || item.recurrence.endDate || undefined,
              noEndDate: Boolean(item.recurrence.no_end_date || item.recurrence.noEndDate),
              materializedUntil: item.recurrence.materialized_until || item.recurrence.materializedUntil || undefined,
            }
          : null

        return {
          id: String(item.id),
          title: item.title,
          date: formatDateLabel(item.start_time),
          time: formatTimeLabel(item.start_time, item.end_time),
          startTimeIso: item.start_time,
          endTimeIso: item.end_time,
          timeZone: item.time_zone,
          cohortId: item.cohort_id ? String(item.cohort_id) : null,
          status: item.can_join_video ? "upcoming" : item.status === "past" ? "past" : "upcoming",
          attendanceTracked: Boolean(item.attendance_tracked),
          isVirtual: !!item.is_virtual,
          guestAccessEnabled: Boolean(item.guest_access_enabled),
          waitingRoomEnabled: Boolean(item.waiting_room_enabled),
          guestJoinUrl: item.guest_join_url || null,
          canJoinVideo: Boolean(item.can_join_video),
          activeParticipantCount: Number(item.active_participant_count || 0),
          lateJoinUntil: item.late_join_until || null,
          manuallyEndedAt: item.manually_ended_at || null,
          questionCollectionEnabled: Boolean(item.question_collection_enabled),
          questionSubmissionCount: Number(item.question_submission_count || 0),
          unreviewedQuestionSubmissionCount: Number(item.unreviewed_question_submission_count || 0),
          virtualLink: item.video_url || undefined,
          description: item.description || "",
          summaryText: item.summary_text || null,
          transcriptionStatus: item.transcription_status,
          transcriptText: item.transcript_text || null,
          recordingEnabled: Boolean(item.recording_enabled),
          recordingStatus: item.recording_status || null,
          recordingAvailable: Boolean(item.recording_available),
          location: item.location || "",
          createdById: item.host?.id ? String(item.host.id) : undefined,
          isMilestone: Boolean(item.is_milestone),
          type: "Meeting",
          attachments: mapAttachments(item.resources),
          invitedUserIds,
          inviteesMode,
          recurrence,
          recurrenceSeriesId: item.series_id || item.recurrence?.series_id || null,
          isException: Boolean(item.is_exception),
          occurrenceStatus: item.occurrence_status || (item.status === "cancelled" ? "cancelled" : item.is_exception ? "edited" : "normal"),
        }
      })
      setEvents(mappedEvents)
      const roster = Array.isArray((data as any)?.roster) ? (data as any).roster : []
      setRosterPeople(roster.map(normalizeRosterPerson))
      setChecklistSummary(normalizeChecklistSummary((data as any)?.checklist_summary))
      const signals = Array.isArray((data as any)?.safety_signals) ? (data as any).safety_signals : []
      setSafetySignalsList(signals as SafetySignal[])
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Unable to load dashboard"
      setDashboardError(msg)
      setDashboardCohort(null)
      setEvents([])
      setRosterPeople([])
      setChecklistSummary(null)
      setSafetySignalsList([])
      setEventsError(msg)
    } finally {
      if (!silent) {
        setDashboardLoading(false)
        setEventsLoading(false)
      }
    }
  }

  const loadLifeScores = async (cohortId: string, userId?: string) => {
    setLifeScoresLoading(true)
    setLifeScoresError(null)
    try {
      const payload = await lifeScores.getReport({
        cohort_id: cohortId,
        user_id: userId && userId !== "all" ? userId : undefined,
      })
      setLifeScoresReport(payload)
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Unable to load life scores"
      setLifeScoresError(msg)
      setLifeScoresReport(null)
    } finally {
      setLifeScoresLoading(false)
    }
  }

  const loadFlows = useCallback(async () => {
    setLoadingFlows(true)
    try {
      const response = await flowApi.getAll()
      const data = (response.data as any)?.results ?? response.data
      setFlows(Array.isArray(data) ? (data as FlowSummary[]) : [])
    } catch (error) {
      console.error("Failed to load flows", error)
      addToast({ title: "Error", description: "Unable to load modules.", variant: "destructive" })
    } finally {
      setLoadingFlows(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!requestedCohortIsUnavailable) return

    const params = new URLSearchParams(location.search)
    if (fallbackCohort?.id) {
      params.set("cohortId", fallbackCohort.id)
    } else {
      params.delete("cohortId")
    }
    params.delete("meetingId")
    const search = params.toString()
    setCohortRecoveryNotice(
      fallbackCohort
        ? `That cohort is no longer available to you. Showing ${fallbackCohort.name} instead.`
        : "That cohort is no longer available to you.",
    )
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash,
      },
      { replace: true },
    )
  }, [fallbackCohort, location.hash, location.pathname, location.search, navigate, requestedCohortIsUnavailable])

  const updateSafetySignal = (updated: SafetySignal) => {
    setSafetySignalsList((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
    setSelectedSignal((prev) => (prev && prev.id === updated.id ? updated : prev))
  }

  useEffect(() => {
    loadDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohortId])

  useEffect(() => {
    if (!selectedCohortId) return
    loadLifeScores(selectedCohortId, lifeScoresUserId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohortId, lifeScoresUserId])

  useEffect(() => {
    if (!selectedSignal || selectedSignal.viewed_at) return
    let active = true
    const markViewed = async () => {
      try {
        const updated = await safetySignals.markViewed(selectedSignal.id)
        if (active) {
          updateSafetySignal(updated as SafetySignal)
        }
      } catch (err: any) {
        const message = err?.response?.data?.detail || err?.message || "Unable to mark safety signal as viewed."
        addToast({ title: "Error", description: message, variant: "destructive" })
      }
    }
    markViewed()
    return () => {
      active = false
    }
  }, [selectedSignal, addToast])

  useEffect(() => {
    if (!selectedSignal) {
      setSignalNoteDraft("")
      setSignalNoteError("")
      setSignalConfirmAction(null)
      return
    }
    setSignalNoteDraft(selectedSignal.admin_note || "")
    setSignalNoteError("")
    setSignalConfirmAction(null)
  }, [selectedSignal])

  const adminEventsList: EventsListItem[] = useMemo(() => {
    return [...events].sort((a, b) => {
      if (a.status !== b.status) {
        return a.status === "past" ? 1 : -1
      }
      const aStart = a.startTimeIso ? new Date(a.startTimeIso).getTime() : 0
      const bStart = b.startTimeIso ? new Date(b.startTimeIso).getTime() : 0
      return aStart - bStart
    })
  }, [events])

  const featuredEvent = useMemo(() => {
    const now = Date.now()
    return adminEventsList.find((event) => {
      if (event.canJoinVideo) return true
      const end = event.endTimeIso ? new Date(event.endTimeIso).getTime() : Number.POSITIVE_INFINITY
      return event.status !== "past" && end >= now
    }) ?? null
  }, [adminEventsList])

  const attendanceReviewCount = useMemo(
    () => adminEventsList.filter((event) => event.status === "past" && !event.attendanceTracked).length,
    [adminEventsList],
  )

  const programMilestones = useMemo(() => adminEventsList
    .filter((event) => event.isMilestone && event.startTimeIso)
    .map((event) => ({ id: event.id, name: event.title, date: event.startTimeIso as string })), [adminEventsList])

  const checklistCompletionPercent = useMemo(() => {
    if (!checklistSummary) return null
    const roles = [checklistSummary.mentors, checklistSummary.scholars]
    const completed = roles.reduce(
      (sum, role) => sum + role.templates.reduce((templateSum, template) => templateSum + template.completedCount, 0),
      0,
    )
    const assigned = roles.reduce((sum, role) => sum + role.totalUsers * role.templates.length, 0)
    return assigned ? Math.round((completed / assigned) * 100) : null
  }, [checklistSummary])

  const rosterSummary = useMemo(() => {
    const mentorCount = rosterPeople.filter((person) => person.roleKey === "mentor").length
    const scholarCount = rosterPeople.filter((person) => person.roleKey === "mentee" || !person.roleKey).length
    return `${mentorCount} ${lowerLabel(getInviteeBucketLabel("mentors", cohortRoleLabels))} · ${scholarCount} ${lowerLabel(getInviteeBucketLabel("scholars", cohortRoleLabels))}`
  }, [cohortRoleLabels, rosterPeople])

  const openMeetingRoom = useCallback((event: EventsListItem) => {
    const target = event.guestAccessEnabled && event.guestJoinUrl
      ? event.guestJoinUrl
      : `/meetings/${event.id}`
    openMeetingEntry({ meetingId: event.id, guestJoinUrl: event.guestAccessEnabled ? event.guestJoinUrl : null, navigate, windowTarget: target })
  }, [navigate])

  const filteredSafetySignals = useMemo(() => {
    const filterMap = {
      new: "new",
      "in-progress": "in_progress",
      complete: "complete",
    } as const
    const targetStatus = filterMap[filterFlags]
    const priorityRank: Record<SafetySignal["priority"], number> = {
      high: 0,
      medium: 1,
      low: 2,
    }

    return [...safetySignalsList]
      .filter((signal) => signal.status === targetStatus)
      .sort((a, b) => {
        const priorityDelta = priorityRank[a.priority] - priorityRank[b.priority]
        if (priorityDelta !== 0) return priorityDelta
        const aTime = new Date(a.created_at).getTime()
        const bTime = new Date(b.created_at).getTime()
        return aTime - bTime
      })
  }, [safetySignalsList, filterFlags])

  useEffect(() => {
    loadFlows()
  }, [loadFlows])

  const resetChecklistDraft = (role = checklistRole) => {
    setChecklistDraft({
      title: "",
      description: "",
      dueDate: "",
      externalUrl: "",
      questionFlowId: NO_FLOW_VALUE,
      role,
    })
    setChecklistError("")
    setChecklistMediaAsset(null)
    setChecklistMediaError("")
    setChecklistAttachmentMode("none")
    setEditingTemplateId(null)
    setChecklistMode("create")
  }

  const handleOpenChecklistModal = () => {
    if (!cohortStartDate) {
      addToast({
        title: "Cohort start date required",
        description: "Set a cohort start date before creating checklist items.",
      })
      return
    }
    setChecklistMode("create")
    setEditingTemplateId(null)
    resetChecklistDraft(checklistRole)
    setChecklistModalOpen(true)
  }

  const handleEditChecklistTemplate = (templateId: string, role: "mentors" | "scholars") => {
    if (!cohortStartDate) {
      addToast({
        title: "Cohort start date required",
        description: "Set a cohort start date before editing checklist items.",
      })
      return
    }
    const roleSummary = checklistSummary?.[role]
    const template = roleSummary?.templates.find((item) => item.id === templateId)
    if (!template) {
      addToast({ title: "Checklist item not found", description: "Select a checklist item to edit." })
      return
    }
    setChecklistMode("edit")
    setEditingTemplateId(templateId)
    setChecklistDraft({
      title: template.title || "",
      description: template.description || "",
      dueDate: template.dueDate || "",
      externalUrl: template.externalUrl || "",
      questionFlowId: NO_FLOW_VALUE,
      role,
    })
    setChecklistMediaAsset(template.mediaAsset || null)
    setChecklistAttachmentMode(template.mediaAsset ? "file" : template.externalUrl ? "link" : "none")
    setChecklistError("")
    setChecklistModalOpen(true)
  }

  const handleChecklistDraftChange = (
    field: "title" | "description" | "dueDate" | "externalUrl" | "role" | "questionFlowId",
    value: string,
  ) => {
    setChecklistDraft((prev) => ({ ...prev, [field]: value }))
    setChecklistError("")
  }

  const handleChecklistExternalUrlChange = (value: string) => {
    setChecklistAttachmentMode("link")
    setChecklistDraft((prev) => ({
      ...prev,
      externalUrl: value,
      ...(value.trim()
        ? {
            questionFlowId: NO_FLOW_VALUE,
          }
        : {}),
    }))
    if (value.trim()) {
      setChecklistMediaAsset(null)
    }
    setChecklistError("")
  }

  const handleChecklistFlowChange = (value: string) => {
    setChecklistAttachmentMode(value === NO_FLOW_VALUE ? "none" : "flow")
    setChecklistDraft((prev) => ({
      ...prev,
      questionFlowId: value,
      ...(value !== NO_FLOW_VALUE
        ? {
            externalUrl: "",
          }
        : {}),
    }))
    if (value !== NO_FLOW_VALUE) {
      setChecklistMediaAsset(null)
    }
    setChecklistError("")
  }

  const flowOptions = useMemo(
    () =>
      flows.map((flow) => ({
        value: String(flow.id),
        label: flow.name,
        searchText: `${flow.name} ${flow.description ?? ""}`.trim(),
      })),
    [flows]
  )

  const flowEmptyMessage = loadingFlows
    ? "Loading modules..."
    : flowSearchTerm.trim()
      ? `No modules found matching "${flowSearchTerm}"`
      : "No modules available"

  const handleChecklistMediaUploadClick = () => {
    setChecklistAttachmentMode("file")
    setChecklistMediaError("")
    checklistMediaInputRef.current?.click()
  }

  const handleChecklistMediaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setChecklistMediaError("")
    setChecklistMediaUploading(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")

      const lookup = await media.lookup(hashHex)
      if (lookup.found && lookup.assetId && lookup.key) {
        const assetId = lookup.assetId as string
        const storageKey = lookup.key as string
        setChecklistMediaAsset({
          id: assetId,
          storage_key: storageKey,
          media_type: toMediaType(lookup.mediaType),
          mime_type: lookup.mimeType ?? null,
          is_active: true,
          url: undefined,
        })
        setChecklistDraft((prev) => ({
          ...prev,
          externalUrl: "",
          questionFlowId: NO_FLOW_VALUE,
        }))
        setChecklistAttachmentMode("file")
        return
      }

      const { key, assetId, mediaType, mimeType } = await media.upload(file, {
        visibility: "private",
        folder: "checklist-item-templates",
        hash: hashHex,
      })

      if (!assetId) {
        throw new Error("Upload succeeded without an asset id.")
      }

      setChecklistMediaAsset({
        id: assetId,
        storage_key: key,
        media_type: toMediaType(mediaType),
        mime_type: mimeType ?? null,
        is_active: true,
        url: undefined,
      })
      setChecklistDraft((prev) => ({
        ...prev,
        externalUrl: "",
        questionFlowId: NO_FLOW_VALUE,
      }))
      setChecklistAttachmentMode("file")
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to upload media."
      setChecklistMediaError(message)
    } finally {
      setChecklistMediaUploading(false)
      if (event.target) event.target.value = ""
    }
  }

  const handleChecklistMediaRemove = () => {
    setChecklistMediaAsset(null)
    setChecklistAttachmentMode("none")
  }

  const handleChecklistAttachmentModeChange = (mode: ChecklistAttachmentMode) => {
    setChecklistAttachmentMode(mode)
    setChecklistError("")
    setChecklistMediaError("")
    setChecklistDraft((prev) => ({
      ...prev,
      externalUrl: mode === "link" ? prev.externalUrl : "",
      questionFlowId: mode === "flow" ? prev.questionFlowId : NO_FLOW_VALUE,
    }))
    if (mode !== "file") setChecklistMediaAsset(null)
  }

  const handleSaveChecklistItem = async () => {
    setChecklistError("")
    if (!activeCohort?.id) {
      addToast({ title: "No active cohort", description: "Select an active cohort before creating a checklist item." })
      setChecklistError("Select an active cohort before creating a checklist item.")
      return
    }
    if (!cohortStartDate) {
      addToast({
        title: "Cohort start date required",
        description: "Set a cohort start date before creating checklist items.",
      })
      setChecklistError("Set a cohort start date before creating checklist items.")
      return
    }
    if (!checklistDraft.title.trim()) {
      addToast({ title: "Title required", description: "Enter a checklist item title." })
      setChecklistError("Enter a checklist item title.")
      return
    }
    if (!checklistDraft.dueDate.trim()) {
      addToast({ title: "Due date required", description: "Select a due date for this checklist item." })
      setChecklistError("Select a due date for this checklist item.")
      return
    }
    const dateParts = checklistDraft.dueDate.split("-").map(Number)
    if (dateParts.length !== 3 || dateParts.some((part) => Number.isNaN(part))) {
      addToast({ title: "Invalid due date", description: "Select a valid due date." })
      setChecklistError("Select a valid due date.")
      return
    }
    const [dueYear, dueMonth, dueDay] = dateParts
    const dueDateOnly = new Date(dueYear, dueMonth - 1, dueDay)
    if (Number.isNaN(dueDateOnly.getTime())) {
      addToast({ title: "Invalid due date", description: "Select a valid due date." })
      setChecklistError("Select a valid due date.")
      return
    }
    const startDateValue = new Date(cohortStartDate.getFullYear(), cohortStartDate.getMonth(), cohortStartDate.getDate())
    if (dueDateOnly <= startDateValue) {
      addToast({
        title: "Due date must be after cohort start",
        description: "Select a due date after the cohort start date.",
      })
      setChecklistError("Select a due date after the cohort start date.")
      return
    }
    const hasFlow = checklistDraft.questionFlowId !== NO_FLOW_VALUE
    const hasUrl = Boolean(checklistDraft.externalUrl.trim())
    const hasMedia = Boolean(checklistMediaAsset?.id)
    if ([hasFlow, hasUrl, hasMedia].filter(Boolean).length > 1) {
      addToast({
        title: "Choose one option",
        description: "Checklist items can only include one of: module, external URL, or media.",
        variant: "destructive",
      })
      setChecklistError("Checklist items can only include one of: module, external URL, or media.")
      return
    }
    if (checklistMode === "edit" && !editingTemplateId) {
      addToast({ title: "Checklist item missing", description: "Select a checklist item to edit." })
      setChecklistError("Select a checklist item to edit.")
      return
    }

    setChecklistSaving(true)
    try {
      const roleKey = checklistDraft.role === "mentors" ? "mentor" : "mentee"
      if (checklistMode === "edit") {
        await adminContent.cohorts.bulkUpdateChecklistItems(activeCohort.id, {
          role: roleKey,
          template_id: editingTemplateId as string,
          title: checklistDraft.title.trim(),
          description: checklistDraft.description.trim(),
          due_date: checklistDraft.dueDate,
          external_url: normalizeExternalUrl(checklistDraft.externalUrl),
          media_asset_id: checklistMediaAsset?.id ?? null,
        })
        addToast({ title: "Checklist items updated" })
      } else {
        const toUtcDate = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        const offsetDays = Math.round((toUtcDate(dueDateOnly) - toUtcDate(startDateValue)) / (1000 * 60 * 60 * 24))
        const parsedFlowId = Number(checklistDraft.questionFlowId)
        const templatePayload = {
          title: checklistDraft.title.trim(),
          description: checklistDraft.description.trim(),
          due_offset_days: Number.isFinite(offsetDays) ? offsetDays : null,
          external_url: normalizeExternalUrl(checklistDraft.externalUrl),
          media_asset_id: checklistMediaAsset?.id ?? null,
          is_active: true,
          question_flow:
            checklistDraft.questionFlowId !== NO_FLOW_VALUE && Number.isFinite(parsedFlowId)
              ? parsedFlowId
              : null,
        }
        const templateResponse = await adminContent.checklistItemTemplates.create(templatePayload)
        const templateId = templateResponse?.data?.id ? String(templateResponse.data.id) : ""
        if (!templateId) {
          throw new Error("Template ID missing from response")
        }

        const assignmentsResponse = await adminContent.cohorts.getAssignments(activeCohort.id)
        const roleAssignments = (assignmentsResponse.data as any)?.[roleKey] || {
          template_ids: [],
          template_group_ids: [],
        }
        const templateIds = new Set([...(roleAssignments.template_ids || []), templateId].filter(Boolean))
        await adminContent.cohorts.updateAssignments(activeCohort.id, {
          role: roleKey,
          template_ids: Array.from(templateIds),
          template_group_ids: roleAssignments.template_group_ids || [],
          apply_to_existing: true,
        })

        addToast({ title: "Checklist item created" })
      }
      setChecklistModalOpen(false)
      resetChecklistDraft(checklistRole)
      await loadDashboard(true)
    } catch (error) {
      console.error("Failed to create checklist item", error)
      const message = extractApiErrorMessage(error, "Unable to save the checklist item.")
      addToast({
        title: "Error",
        description: message,
        variant: "destructive",
      })
      setChecklistError(message)
    } finally {
      setChecklistSaving(false)
    }
  }

  const rosterPeopleFiltered = rosterPeople.filter((person) => {
    const q = rosterSearch.trim().toLowerCase()
    if (!q) return true
    return person.name.toLowerCase().includes(q) || person.role.toLowerCase().includes(q)
  })
  const eventRosterPeople = useMemo(() => {
    const members: Array<{ id: string | number; name: string; role?: string; roleKey?: Person["roleKey"]; avatarUrl?: string | null }> = rosterPeople.map((person) => ({
      id: person.id,
      name: person.name,
      role: person.role,
      roleKey: person.roleKey,
      avatarUrl: person.avatarUrl,
    }))
    if (currentUser && !members.some((person) => String(person.id) === String(currentUser.id))) {
      const fullName = `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
      members.push({
        id: currentUser.id,
        name: fullName || currentUser.username || "Current user",
        role: currentUser.role ? getCohortRoleLabel(currentUser.role, cohortRoleLabels) : undefined,
        roleKey: currentUser.role,
        avatarUrl: currentUser.avatar_url ?? null,
      })
    }
    return members
  }, [cohortRoleLabels, currentUser, rosterPeople])

  const handlePersonUpdated = (updated: Person) => {
    setRosterPeople((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
    setSelectedPerson(updated)
  }

  const handleSavePairings = async (updates: Array<{ userId: number; mentorId: number | null }>) => {
    if (!activeCohort?.id) {
      throw new Error("Select a cohort before managing mentor pairings.")
    }
    await adminContent.cohorts.updateMentorPairings(activeCohort.id, {
      pairings: updates.map((update) => ({
        user_id: update.userId,
        mentor_id: update.mentorId,
      })),
    })
    void refreshContinuity()
    setRosterPeople((current) => {
      const mentorNames = new Map(current.map((person) => [person.id, person.name]))
      const updatesByUser = new Map(updates.map((update) => [update.userId, update.mentorId]))
      return current.map((person) => {
        if (!updatesByUser.has(person.id)) return person
        const mentorId = updatesByUser.get(person.id) ?? null
        return {
          ...person,
          mentorId,
          mentorName: mentorId == null ? null : mentorNames.get(mentorId) || null,
        }
      })
    })
    addToast({ title: "Mentor pairings updated." })
  }

  const handleSignalAction = async (
    action: () => Promise<SafetySignal>,
    successTitle: string,
  ): Promise<boolean> => {
    setSignalActionLoading(true)
    try {
      const updated = await action()
      updateSafetySignal(updated)
      addToast({ title: successTitle })
      return true
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || "Unable to update the safety signal."
      addToast({ title: "Error", description: message, variant: "destructive" })
      return false
    } finally {
      setSignalActionLoading(false)
    }
  }

  const handleNotifyMentor = async () => {
    if (!selectedSignal) return
    const didUpdate = await handleSignalAction(
      () => safetySignals.notifyMentor(selectedSignal.id),
      `${getCohortRoleLabel("mentor", cohortRoleLabels)} notified`,
    )
    if (didUpdate) setSignalConfirmAction(null)
  }

  const handleResolveSignal = async () => {
    if (!selectedSignal) return
    const didUpdate = await handleSignalAction(
      () => safetySignals.resolve(selectedSignal.id),
      "Safety signal resolved",
    )
    if (didUpdate) {
      setSelectedSignal(null)
    }
  }

  const handleDismissSignal = async () => {
    if (!selectedSignal) return
    const didUpdate = await handleSignalAction(
      () => safetySignals.dismiss(selectedSignal.id),
      "Safety signal dismissed",
    )
    if (didUpdate) {
      setSelectedSignal(null)
    }
  }

  const handleClearCompletion = () => {
    if (!selectedSignal) return
    return handleSignalAction(
      () => safetySignals.clearCompletion(selectedSignal.id),
      "Safety signal re-opened",
    )
  }

  const handleDownloadSignalTranscript = async () => {
    if (!selectedSignal) return
    const meetingId = (selectedSignal.metadata as any)?.meeting_id
    if (!meetingId) {
      addToast({ title: "Transcript unavailable", description: "No meeting linked to this signal." })
      return
    }
    try {
      const blob = await meetings.downloadTranscript(String(meetingId))
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `meeting-${meetingId}-transcript.txt`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || "Unable to download transcript."
      addToast({ title: "Download failed", description: message, variant: "destructive" })
    }
  }

  const handleSaveSignalNote = async () => {
    if (!selectedSignal) return
    setSignalNoteSaving(true)
    setSignalNoteError("")
    try {
      const updated = await safetySignals.updateNote(selectedSignal.id, signalNoteDraft)
      updateSafetySignal(updated as SafetySignal)
      setSignalNoteDraft((updated as SafetySignal)?.admin_note || "")
      addToast({ title: "Admin note saved" })
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || "Unable to save the admin note."
      setSignalNoteError(message)
      addToast({ title: "Error", description: message, variant: "destructive" })
    } finally {
      setSignalNoteSaving(false)
    }
  }

  const renderSignalMetadata = () => {
    if (!selectedSignal) return null
    const metadata = selectedSignal.metadata as Record<string, any> | null
    if (!metadata || Object.keys(metadata).length === 0) return null
    const evidence = Array.isArray(metadata.evidence) ? metadata.evidence : []
    return (
      <section data-testid="safety-signal-reported-context" className="space-y-2">
        <h3 className="text-base">Reported context</h3>
        {metadata.category && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Category:</span> {String(metadata.category)}
          </p>
        )}
        {metadata.type && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Type:</span> {String(metadata.type)}
          </p>
        )}
        {metadata.severity && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Severity:</span> {String(metadata.severity)}
          </p>
        )}
        {metadata.confidence !== undefined && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Confidence:</span> {String(metadata.confidence)}
          </p>
        )}
        {metadata.speaker_name && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Speaker:</span> {String(metadata.speaker_name)}
          </p>
        )}
        {(metadata.reported_by_user_name || metadata.reported_by_user_id) && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Reported by:</span>{" "}
            {metadata.reported_by_user_name
              ? String(metadata.reported_by_user_name)
              : `User #${String(metadata.reported_by_user_id)}`}
          </p>
        )}
        {(metadata.reported_user_name || metadata.reported_user_id) && (
          <p className="text-sm text-[var(--cohort-ink-muted)]">
            <span className="font-medium">Reported user:</span>{" "}
            {metadata.reported_user_name
              ? String(metadata.reported_user_name)
              : `User #${String(metadata.reported_user_id)}`}
          </p>
        )}
        {evidence.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--cohort-ink-muted)]">Evidence (paraphrased)</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-[var(--cohort-ink-muted)]">
              {evidence.map((item: any, index: number) => (
                <li key={`${index}-${item?.timestamp || "evidence"}`}>
                  {item?.timestamp ? `[${item.timestamp}] ` : ""}
                  {item?.snippet || ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    )
  }

  const isDashboardLoading = dashboardLoading || eventsLoading

  if (isDashboardLoading) {
    return <CohortLoadingState admin />
  }

  if (dashboardError) {
    return <CohortFailureState admin onRetry={() => void loadDashboard()} />
  }

  const signalDialogBusy = signalActionLoading || signalNoteSaving
  const checklistDialogBusy = checklistSaving || checklistMediaUploading
  const checklistTitleError = checklistError === "Enter a checklist item title." ? checklistError : ""
  const checklistDueDateError = [
    "Select a due date for this checklist item.",
    "Select a valid due date.",
    "Select a due date after the cohort start date.",
  ].includes(checklistError) ? checklistError : ""
  const checklistAttachmentError = checklistError.startsWith("Checklist items can only include") ? checklistError : ""
  const checklistGlobalError = checklistError && !checklistTitleError && !checklistDueDateError && !checklistAttachmentError
    ? checklistError
    : ""

  return (
    <div className="cohort-experience cohort-page-surface">
      <div className="cohort-admin-layout mx-auto max-w-6xl space-y-7">
        {cohortRecoveryNotice ? (
          <div role="status" className="rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-paper-strong)] px-4 py-3 text-sm text-[var(--cohort-ink)]">
            {cohortRecoveryNotice}
          </div>
        ) : null}
        <CohortOverviewCard
          cohortName={activeCohort?.name || null}
          startDate={cohortStartDate}
          endDate={cohortEndDate}
          memberCount={rosterPeople.length}
          memberSummary={rosterSummary}
          newSignalsCount={safetySignalsList.filter((signal) => signal.status === "new").length}
          attendanceReviewCount={attendanceReviewCount}
          checklistCompletionPercent={checklistCompletionPercent}
          manageableCohortCount={cohortSwitcherOptions.length}
          selector={cohortSwitcherOptions.length > 1 ? (
            <Select
              value={selectedCohortId ?? activeCohort?.id ?? cohortSwitcherOptions[0]?.id}
              onValueChange={handleDashboardCohortChange}
            >
              <SelectTrigger id="dashboard-cohort-selector" aria-label="Managing cohort" className="cohort-admin-selector-trigger min-h-11 w-full rounded-xl border-[var(--cohort-rule)] bg-white px-4 text-left shadow-none">
                <SelectValue placeholder="Select cohort" />
              </SelectTrigger>
              <SelectContent>
                {cohortSwitcherOptions.map((cohort) => (
                  <SelectItem key={cohort.id} value={cohort.id}>{cohort.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        />

        <CohortEventHero
          viewerTimeZone={currentUser?.timezone}
          event={featuredEvent}
          canCreate
          isAdmin
          currentUserId={currentUser?.id}
          roster={rosterPeople}
          onOpenEvent={(event) => eventsCardRef.current?.openEventDetails(event.id)}
          onManageEvent={(event) => eventsCardRef.current?.openManageEvent(event.id)}
          onCreateEvent={() => eventsCardRef.current?.openCreateEvent()}
          onJoinEvent={openMeetingRoom}
        />

        <nav className="cohort-mobile-bleed cohort-admin-mobile-tabs sticky top-0 z-40 grid grid-cols-3 border-b border-[var(--cohort-rule)] bg-[var(--cohort-elevated)] px-5 lg:hidden" aria-label="Dashboard sections">
          <div className="contents" role="tablist" aria-label="Admin dashboard sections">
            {ADMIN_MOBILE_TABS.map((tab, index) => {
              const isSelected = activeMobileTab === tab.id
              return (
                <button
                  key={tab.id}
                  ref={(node) => { mobileTabRefs.current[tab.id] = node }}
                  id={`admin-mobile-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  aria-controls={`admin-mobile-panel-${tab.id}`}
                  tabIndex={isSelected ? 0 : -1}
                  className="cohort-admin-mobile-tab flex min-h-[46px] items-center justify-center border-0 bg-transparent text-sm"
                  onClick={() => selectMobileTab(tab.id)}
                  onKeyDown={(event) => handleMobileTabKeyDown(event, index)}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </nav>

        <section
          id="admin-mobile-panel-attention"
          role="tabpanel"
          aria-labelledby="admin-mobile-tab-attention"
          className={`cohort-admin-dashboard-section cohort-admin-dashboard-section--first ${activeMobileTab === "attention" ? "" : "hidden"} lg:block`}
        >
          <div className="cohort-admin-section-grid grid gap-x-12 gap-y-7 lg:grid-cols-12">
            <FlagsCard
              signals={filteredSafetySignals}
              filter={filterFlags}
              counts={{
                new: safetySignalsList.filter((signal) => signal.status === "new").length,
                "in-progress": safetySignalsList.filter((signal) => signal.status === "in_progress").length,
                complete: safetySignalsList.filter((signal) => signal.status === "complete").length,
              }}
              onFilterChange={setFilterFlags}
              onSelectSignal={setSelectedSignal}
              className="cohort-admin-band cohort-admin-band--sand cohort-admin-band--safety lg:col-span-5"
            />
            <EventsListCard
              ref={eventsCardRef}
              title="Events & meetings"
              events={adminEventsList}
              loading={eventsLoading}
              error={eventsError}
              showPast={showPastEvents}
              onTogglePast={(checked) => {
                setShowPastEvents(!!checked)
                if (checked) setShowRequiresAttendance(false)
              }}
              showRequiresAttendance={showRequiresAttendance}
              onToggleRequiresAttendance={(checked) => {
                setShowRequiresAttendance(!!checked)
                if (checked) setShowPastEvents(false)
              }}
              requiresAttendanceLabel={`Needs attendance ${attendanceReviewCount}`}
              isAdmin
              currentUserId={currentUser ? String(currentUser.id) : undefined}
              cohortId={activeCohort?.id ? String(activeCohort.id) : undefined}
              canCreateEvent
              roster={eventRosterPeople}
              roleLabels={cohortRoleLabels}
              deepLinkedMeetingId={meetingIdParam}
              buildMeetingDetailsUrl={buildMeetingDetailsUrl}
              onMeetingPopupUrlChange={handleMeetingPopupUrlChange}
              onEventsChange={setEvents}
              continuityRelationships={continuityRelationships}
              onOpenContinuity={openContinuity}
              className="cohort-admin-band cohort-admin-band--white cohort-admin-band--events lg:col-span-7"
            />
          </div>
        </section>

        <section
          id="admin-mobile-panel-manage"
          role="tabpanel"
          aria-labelledby="admin-mobile-tab-manage"
          className={`cohort-admin-dashboard-section ${activeMobileTab === "manage" ? "" : "hidden"} lg:block`}
        >
          <div className="cohort-admin-section-grid grid gap-x-12 gap-y-7 lg:grid-cols-12">
            <TodosCard
              summary={checklistSummary}
              role={checklistRole}
              onRoleChange={setChecklistRole}
              onCreate={handleOpenChecklistModal}
              onTemplateClick={(templateId) => handleEditChecklistTemplate(templateId, checklistRole)}
              loading={dashboardLoading}
              error={dashboardError}
              canCreate={Boolean(cohortStartDate)}
              roleLabels={cohortRoleLabels}
              className="cohort-admin-band cohort-admin-band--sand cohort-admin-band--checklist lg:col-span-7"
            />
            <div className="cohort-admin-manage-rail contents lg:col-span-5 lg:flex lg:flex-col lg:gap-7">
              <div data-testid="cohort-admin-pairings-band" className="cohort-admin-band cohort-admin-band--white cohort-admin-band--pairings lg:contents">
                <MentorPairingsCard
                  display="responsive"
                  roster={rosterPeople}
                  mentorLabelSingular={configuredRoleLabels.mentorSingular}
                  mentorLabelPlural={configuredRoleLabels.mentorPlural}
                  memberLabelSingular={configuredRoleLabels.menteeSingular}
                  memberLabelPlural={configuredRoleLabels.menteePlural}
                  onSavePairings={handleSavePairings}
                  onSelectPerson={setSelectedPerson}
                />
                {continuityRelationships.length > 0 || continuityError ? <section className="mt-5 rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-5" aria-label="Mentorship continuity oversight">
                  <h2 className="text-xl font-normal">Relationship notes</h2>
                  <p className="mt-2 text-sm leading-5 text-[var(--cohort-ink-muted)]">Review recaps and follow-ups within your existing cohort access. Mentor assignments make these available automatically.</p>
                  {continuityError ? <div role="alert" className="mt-3 text-sm"><p>{continuityError}</p><button type="button" onClick={() => void refreshContinuity()} className="cohort-text-action min-h-11">Try again</button></div> : continuityRelationships.map(relationship => <button type="button" key={relationship.id} onClick={() => openContinuity(relationship.id)} className="cohort-row-action mt-3 flex min-h-14 w-full flex-col items-start border-t border-[var(--cohort-rule-soft)] pt-3 text-left">
                    <span className="text-sm font-medium">{relationship.mentee.name} &amp; {relationship.mentor.name}</span>
                    <span className="mt-1 text-xs text-[var(--cohort-ink-muted)]">{getContinuityCue(relationship)}</span>
                  </button>)}
                </section> : null}
              </div>
              <MessagesCard
                recipient={messageRecipient}
                onRecipientChange={setMessageRecipient}
                subject={messageSubject}
                onSubjectChange={setMessageSubject}
                messageBody={messageBody}
                onMessageBodyChange={setMessageBody}
                channel={messageChannels}
                onChannelChange={setMessageChannels}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                roster={rosterPeople}
                currentUserId={currentUser ? String(currentUser.id) : undefined}
                selectedIndividuals={selectedIndividuals}
                onSelectedIndividualsChange={setSelectedIndividuals}
                roleLabels={cohortRoleLabels}
                className="cohort-admin-band cohort-admin-band--sand cohort-admin-band--messages"
              />
              <div data-testid="cohort-admin-program-band" className="cohort-admin-band cohort-admin-band--white cohort-admin-band--program lg:contents">
                <ProgramCard
                  startDate={cohortStartDate}
                  endDate={cohortEndDate}
                  milestones={programMilestones}
                  onEditDates={() => navigate(`/admin/cohorts${activeCohort?.id ? `?cohortId=${encodeURIComponent(activeCohort.id)}` : ""}`)}
                />
              </div>
            </div>
          </div>
        </section>

        <section
          id="admin-mobile-panel-people"
          role="tabpanel"
          aria-labelledby="admin-mobile-tab-people"
          className={`cohort-admin-dashboard-section ${activeMobileTab === "people" ? "" : "hidden"} lg:block`}
        >
          <div className="cohort-admin-section-grid grid gap-x-12 gap-y-7 lg:grid-cols-12">
            <RosterCard
              mobileEyebrow="Roster"
              mobileTitle="Who's in this cohort"
              roster={rosterPeopleFiltered}
              searchValue={rosterSearch}
              onSearchChange={setRosterSearch}
              onSelectPerson={setSelectedPerson}
              showProgress
              roleLabels={cohortRoleLabels}
              className="cohort-admin-band cohort-admin-band--sand cohort-admin-band--roster lg:col-span-5"
            />
            <ReportsTrendsCard
              mobileEyebrow="Participation"
              mobileTitle="Reports & trends"
              data={lifeScoresReport}
              loading={lifeScoresLoading}
              error={lifeScoresError}
              roster={rosterPeople}
              selectedUserId={lifeScoresUserId}
              onUserChange={setLifeScoresUserId}
              className="cohort-admin-band cohort-admin-band--white cohort-admin-band--participation lg:col-span-7"
            />
          </div>
        </section>
      </div>

      <PersonSidePanel
        key={selectedPerson?.id || "none"}
        person={selectedPerson}
        onClose={() => setSelectedPerson(null)}
        onUpdatePerson={handlePersonUpdated}
      />

      {selectedSignal ? (
        <AppDialog
          onClose={() => {
            if (!signalDialogBusy) setSelectedSignal(null)
          }}
          size="sm"
          className="cohort-dialog cohort-dialog--framed cohort-dialog--signal max-h-[calc(100dvh-2rem)] max-w-[680px]"
          title={<><span className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-[var(--cohort-clay)]"><span>{selectedSignal.priority} priority</span><span aria-hidden="true">·</span><span>{selectedSignal.status === "in_progress" ? "In progress" : selectedSignal.status === "complete" ? "Complete" : "New"}</span></span><span className="block">Safety signal · {selectedSignal.user_name}</span></>}
          titleClassName="pr-10 text-[25px] font-normal text-[var(--cohort-ink)]"
          description={`${selectedSignal.source.replace(/_/g, " ")} · Created ${formatSignalTimestamp(selectedSignal.created_at)}`}
          descriptionClassName="text-sm leading-5 text-[var(--cohort-ink-muted)]"
          bodyClassName="space-y-5"
          isBusy={signalDialogBusy}
          actions={
            signalConfirmAction ? (
              <div className="w-full rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3">
                <p className="text-sm font-medium text-[var(--cohort-clay)]">
                  {signalConfirmAction === "notify"
                    ? `Notify ${lowerLabel(getCohortRoleLabel("mentor", cohortRoleLabels))} and create a follow-up checklist item?`
                    : "Dismiss this signal? It will move out of the active review queue."}
                </p>
                <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="secondary" onClick={() => setSignalConfirmAction(null)} disabled={signalDialogBusy} className="cohort-secondary-button">Go back</Button>
                  <Button
                    type="button"
                    onClick={() => void (signalConfirmAction === "notify" ? handleNotifyMentor() : handleDismissSignal())}
                    disabled={signalDialogBusy}
                    className={signalConfirmAction === "dismiss" ? "cohort-destructive-secondary-button" : "cohort-primary-button"}
                  >
                    {signalActionLoading ? "Updating…" : signalConfirmAction === "notify" ? "Notify and follow up" : "Dismiss signal"}
                  </Button>
                </div>
              </div>
            ) : selectedSignal.status === "complete" ? (
              <div className="flex w-full justify-end">
                <Button type="button" onClick={handleClearCompletion} disabled={signalDialogBusy} className="cohort-primary-button">
                  {signalActionLoading ? "Updating…" : "Reopen signal"}
                </Button>
              </div>
            ) : (
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Button type="button" variant="secondary" onClick={() => setSignalConfirmAction("dismiss")} disabled={signalDialogBusy} className="cohort-secondary-button w-full sm:w-auto">Dismiss</Button>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  {selectedSignal.mentor_id && !selectedSignal.mentor_notified_at ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setSignalConfirmAction("notify")}
                      disabled={signalDialogBusy}
                      className="cohort-secondary-button w-full sm:w-auto"
                    >
                      Notify {lowerLabel(getCohortRoleLabel("mentor", cohortRoleLabels))}
                    </Button>
                  ) : null}
                  <Button type="button" onClick={handleResolveSignal} disabled={signalDialogBusy} className="cohort-primary-button min-h-[52px] w-full text-[17px] sm:min-h-[46px] sm:w-auto sm:text-base">
                    {signalActionLoading ? "Updating…" : "Mark resolved"}
                  </Button>
                </div>
              </div>
            )
          }
        >
          <p className="text-[15px] leading-6 text-[var(--cohort-ink)]">{selectedSignal.summary}</p>

          <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-y border-[var(--cohort-rule)] py-4 sm:grid-cols-3">
            <div><p className="text-xs text-[var(--cohort-ink-muted)]">Created</p><p className="mt-1 text-sm">{formatSignalTimestamp(selectedSignal.created_at)}</p></div>
            <div><p className="text-xs text-[var(--cohort-ink-muted)]">Status</p><p className="mt-1 text-sm">{selectedSignal.status === "new" ? "New" : selectedSignal.status === "in_progress" ? "In progress" : "Complete"}</p></div>
            <div><p className="text-xs text-[var(--cohort-ink-muted)]">Last updated by</p><p className="mt-1 text-sm">{selectedSignal.last_updated_by?.name || "Not yet updated"}</p></div>
          </div>

          {selectedSignal.source === "meeting_transcript" ? (
            <section className="cohort-open-section">
              <h3 className="text-base">Source material</h3>
              <p className="mt-1 text-sm text-[var(--cohort-ink-muted)]">This signal came from a meeting transcript. Downloading it may include sensitive participant content.</p>
              <Button variant="secondary" size="auto" className="cohort-secondary-button mt-3" onClick={handleDownloadSignalTranscript}>Download transcript</Button>
            </section>
          ) : null}

          {renderSignalMetadata()}

          <section className="cohort-open-section space-y-2">
            <Label htmlFor="signal-admin-note" className="block border-b border-[var(--cohort-rule)] pb-2 text-lg">Internal note</Label>
            <p className="text-sm leading-5 text-[var(--cohort-ink-muted)]">Visible to cohort administrators, not cohort members.</p>
            <Textarea
              id="signal-admin-note"
              value={signalNoteDraft}
              onChange={(event) => setSignalNoteDraft(event.target.value)}
              placeholder="Add context or the next step for other administrators…"
              rows={4}
              disabled={signalDialogBusy}
              className="rounded-xl border-[var(--cohort-rule)] bg-white text-base"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button size="auto" variant="secondary" onClick={handleSaveSignalNote} disabled={signalDialogBusy} className="cohort-secondary-button">{signalNoteSaving ? "Saving…" : "Save note"}</Button>
              {signalNoteError ? <p className="text-xs text-destructive">{signalNoteError}</p> : null}
            </div>
          </section>

          {selectedSignal.mentor_notified_at ? (
            <section className="cohort-open-section space-y-2">
              <h3 className="text-base">{getCohortRoleLabel("mentor", cohortRoleLabels)} follow-up</h3>
              <p className="text-sm text-[var(--cohort-ink-muted)]">Notified {formatSignalTimestamp(selectedSignal.mentor_notified_at)}</p>
              {selectedSignal.mentor_checklist_item ? (
                <p className="text-sm text-[var(--cohort-ink-muted)]">Checklist follow-up: {selectedSignal.mentor_checklist_item.completed_at ? `Completed ${formatSignalTimestamp(selectedSignal.mentor_checklist_item.completed_at)}` : "Not completed"}</p>
              ) : null}
            </section>
          ) : null}
        </AppDialog>
      ) : null}

      {checklistModalOpen ? (
        <AppDialog
          onClose={() => {
            setChecklistModalOpen(false)
            resetChecklistDraft(checklistRole)
          }}
          size="sm"
          className="cohort-dialog cohort-dialog--framed max-h-[calc(100dvh-2rem)] max-w-[600px]"
          title={checklistMode === "edit" ? "Edit checklist item" : "New checklist item"}
          description={
            checklistMode === "edit"
              ? "Update what people need to do and when it is due."
              : "Assigned to everyone you choose below."
          }
          descriptionClassName="text-sm leading-5 text-[var(--cohort-ink-muted)]"
          bodyClassName="space-y-5"
          titleClassName="pr-10 text-[25px] font-normal"
          isBusy={checklistDialogBusy}
          actions={
            <>
              <div className="flex w-full flex-col gap-2">
                {checklistGlobalError ? <p className="rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-xs text-[var(--cohort-clay)]">{checklistGlobalError}</p> : null}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <DialogClose asChild><Button type="button" variant="secondary" disabled={checklistDialogBusy} className="cohort-secondary-button">Cancel</Button></DialogClose>
                  <Button type="button" onClick={handleSaveChecklistItem} disabled={checklistDialogBusy} className="cohort-primary-button">
                    {checklistSaving ? "Saving…" : checklistMode === "edit" ? "Save changes" : "Create item"}
                  </Button>
                </div>
              </div>
            </>
          }
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="checklist-title" className="text-sm font-medium">What should they do?</Label>
              <Input
                id="checklist-title"
                value={checklistDraft.title}
                onChange={(event) => handleChecklistDraftChange("title", event.target.value)}
                disabled={checklistDialogBusy}
                aria-invalid={Boolean(checklistTitleError)}
                aria-describedby={checklistTitleError ? "checklist-title-error" : undefined}
                placeholder="What needs to be done?"
                className="min-h-12 rounded-xl border-[var(--cohort-rule)] bg-white text-base"
              />
              {checklistTitleError ? <p id="checklist-title-error" className="text-sm text-[var(--cohort-clay)]">{checklistTitleError}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist-description" className="text-sm font-medium">More detail <span className="font-normal text-[var(--cohort-ink-muted)]">— optional</span></Label>
              <Textarea
                id="checklist-description"
                value={checklistDraft.description}
                onChange={(event) => handleChecklistDraftChange("description", event.target.value)}
                disabled={checklistDialogBusy}
                placeholder="Add useful context (optional)"
                className="rounded-xl border-[var(--cohort-rule)] bg-white text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist-due-date" className="text-sm font-medium">Due date</Label>
              <Input
                id="checklist-due-date"
                type="date"
                min={minChecklistDueDate}
                value={checklistDraft.dueDate}
                onChange={(event) => handleChecklistDraftChange("dueDate", event.target.value)}
                disabled={checklistDialogBusy}
                aria-invalid={Boolean(checklistDueDateError)}
                aria-describedby={checklistDueDateError ? "checklist-due-date-error" : undefined}
                className="min-h-12 rounded-xl border-[var(--cohort-rule)] bg-white text-base sm:max-w-[280px]"
              />
              {checklistDueDateError ? <p id="checklist-due-date-error" className="text-sm text-[var(--cohort-clay)]">{checklistDueDateError}</p> : null}
            </div>
            <section className="space-y-3">
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Attach something to open</h3>
              <p className="text-sm leading-5 text-[var(--cohort-ink-muted)]">One item can hold one thing: a module, a link, or a file. Choosing one clears the others.</p>
              <div className="flex flex-wrap gap-2">
                {([
                  ["flow", "Module"],
                  ["link", "A link"],
                  ["file", "A file"],
                  ["none", "Nothing"],
                ] as const).map(([mode, label]) => (
                  <button key={mode} type="button" aria-pressed={checklistAttachmentMode === mode} onClick={() => handleChecklistAttachmentModeChange(mode)} disabled={checklistDialogBusy} className="cohort-choice inline-flex min-h-[46px] items-center gap-2 px-4 text-[15px] disabled:cursor-not-allowed disabled:opacity-60">
                    {checklistAttachmentMode === mode ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{label}
                  </button>
                ))}
              </div>
              {checklistAttachmentMode === "flow" ? (
                <div className="space-y-2">
                  <FlowPicker
                    value={checklistDraft.questionFlowId}
                    onValueChange={handleChecklistFlowChange}
                    options={flowOptions}
                    placeholder="Search modules"
                    searchTerm={flowSearchTerm}
                    onSearchTermChange={setFlowSearchTerm}
                    disabled={loadingFlows || checklistDialogBusy}
                    loading={loadingFlows}
                    emptyMessage={flowEmptyMessage}
                    sentinelOption={{ value: NO_FLOW_VALUE, label: "No module" }}
                    triggerClassName="min-h-12 rounded-xl border-[var(--cohort-rule)] bg-white px-4 text-base"
                    contentClassName="cohort-experience z-[700]"
                  />
                  <p className="text-sm leading-5 text-[var(--cohort-ink-muted)]">Members finish this task by completing the module—there is no tick box for them.</p>
                </div>
              ) : null}
              {checklistAttachmentMode === "link" ? (
                <div className="space-y-2">
                  <Label htmlFor="checklist-external-url" className="sr-only">External link</Label>
                  <Input id="checklist-external-url" value={checklistDraft.externalUrl} onChange={(event) => handleChecklistExternalUrlChange(event.target.value)} disabled={checklistDialogBusy} placeholder="https://" className="min-h-12 rounded-xl border-[var(--cohort-rule)] bg-white text-base" />
                </div>
              ) : null}
              {checklistAttachmentMode === "file" ? (
                <div className="rounded-xl border border-[var(--cohort-rule)] bg-white p-3">
                <input
                  ref={checklistMediaInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleChecklistMediaChange}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    size="auto"
                    variant="secondary"
                    onClick={handleChecklistMediaUploadClick}
                    disabled={checklistDialogBusy}
                    className="cohort-secondary-button"
                  >
                    {checklistMediaUploading ? "Uploading…" : "Choose a file"}
                  </Button>
                  {checklistMediaAsset ? (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                      <span className="font-medium">
                        {checklistMediaAsset.storage_key.split("/").pop() || checklistMediaAsset.id}
                      </span>
                      <Button size="auto" variant="secondary" onClick={handleChecklistMediaRemove} disabled={checklistDialogBusy} className="cohort-secondary-button">
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">No media attached.</span>
                  )}
                </div>
                {checklistMediaError ? <p className="mt-2 text-xs text-destructive">{checklistMediaError}</p> : null}
                </div>
              ) : null}
              {checklistAttachmentError ? <p className="text-sm text-[var(--cohort-clay)]">{checklistAttachmentError}</p> : null}
            </section>
            <section className="space-y-3">
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Who gets it</h3>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <Button
                  size="auto"
                  variant="secondary"
                  aria-pressed={checklistDraft.role === "mentors"}
                  className="cohort-choice min-h-12 gap-2 px-4 text-[15px]"
                  onClick={() => handleChecklistDraftChange("role", "mentors")}
                  disabled={checklistMode === "edit" || checklistDialogBusy}
                >
                  {checklistDraft.role === "mentors" ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{getInviteeBucketLabel("mentors", cohortRoleLabels)} · {checklistSummary?.mentors.totalUsers ?? 0}
                </Button>
                <Button
                  size="auto"
                  variant="secondary"
                  aria-pressed={checklistDraft.role === "scholars"}
                  className="cohort-choice min-h-12 gap-2 px-4 text-[15px]"
                  onClick={() => handleChecklistDraftChange("role", "scholars")}
                  disabled={checklistMode === "edit" || checklistDialogBusy}
                >
                  {checklistDraft.role === "scholars" ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{getInviteeBucketLabel("scholars", cohortRoleLabels)} · {checklistSummary?.scholars.totalUsers ?? 0}
                </Button>
              </div>
              {checklistMode === "edit" ? <p className="text-sm leading-5 text-[var(--cohort-ink-muted)]">Who this item is assigned to can&apos;t change after it has been created.</p> : null}
            </section>
          </div>
        </AppDialog>
      ) : null}
      {activeCohort?.id && continuityOpen ? <ForNextTimeDialog
        open={continuityOpen}
        onOpenChange={setContinuityOpen}
        cohortId={activeCohort.id}
        initialRelationshipId={continuityRelationshipId}
        currentUserId={currentUser ? String(currentUser.id) : undefined}
        readOnly
        renderMeetingActions={(relationship, requestAction) => <RelationshipMeetingActions
          relationship={relationship}
          events={adminEventsList}
          onSchedule={row => requestAction(() => {
            setContinuityOpen(false)
            eventsCardRef.current?.openCreateEvent({ title: `1:1 with ${row.mentee.name}`, inviteesMode: "custom", invitedUserIds: [row.mentor.id, row.mentee.id] })
          })}
        />}
      /> : null}
    </div>
  )
}
